/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { isGoogleImportedEvent } from '@features/agenda/utils/google-event-source';
import { calendarDisplayName } from '@features/agenda/utils/calendar-display';
import { useEventDrawerForm } from './useEventDrawerForm';
import { RECURRENCE_OPTIONS } from './schema';
import { buildFreqLabels } from './drawer-styles';
import { EventDrawerSubmitButton } from './SubmitButton';
import { EventDrawerFields } from './EventDrawerFields';
import type { CalendarEventInput } from '@services/requests/housien-models/CalendarEventInput';
import { Text } from '@/shared/ui';
import { isDevFormBridgeEnabled } from '@/shared/utils/dev-form-bridge';

export interface EventDrawerProps {
  open: boolean;
  onClose: () => void;
  agencyId: string;
  agencyName?: string;
  calendars: Array<{ calendar_id: string; name: string; color?: string; is_primary?: boolean }>;
  editingEvent?: any;
  initialDate?: string;
  initialTime?: string;
  onSubmitCreate: (body: CalendarEventInput) => Promise<void>;
  onSubmitUpdate: (eventId: string, body: CalendarEventInput) => Promise<void>;
}

/**
 * Slide-over drawer for creating or editing a calendar event.
 * @param props - Open state, agency context, calendars, editing target, and
 *   submit/close callbacks.
 * @returns The event drawer element.
 */
export function EventDrawer({
  open,
  onClose,
  agencyId,
  agencyName,
  calendars,
  editingEvent,
  initialDate,
  initialTime,
  onSubmitCreate,
  onSubmitUpdate,
}: EventDrawerProps) {
  const { t } = useTranslation('features/agenda');
  const {
    form,
    isSubmitting,
    hasValidationErrors,
    handleSubmit,
    isEditMode,
  } = useEventDrawerForm({
    agencyId,
    calendars,
    editingEvent,
    initialDate,
    initialTime,
    onSubmitCreate,
    onSubmitUpdate,
    onSuccess: onClose,
  });

  const allDay = form.watch('allDay');

  const calendarData = useMemo(
    () => calendars.map((c) => ({
      value: c.calendar_id,
      label: calendarDisplayName(
        { name: c.name, is_primary: c.is_primary } as any,
        t,
        agencyName ?? '',
      ),
    })),
    [calendars, t, agencyName]
  );
  const freqLabels = useMemo(() => buildFreqLabels(t), [t]);
  const recurrenceData = useMemo(
    () => RECURRENCE_OPTIONS.map((opt) => ({ value: opt, label: freqLabels[opt] })),
    [freqLabels]
  );

  useEffect(() => {
    if (isDevFormBridgeEnabled() && open) {
      (window as any).__DEV_EVENT_FORM__ = {
        setValue: form.setValue,
        getValues: form.getValues,
        trigger: form.trigger,
        submit: handleSubmit,
      };
    }
    return () => { (window as any).__DEV_EVENT_FORM__ = undefined; };
  }, [open, form, handleSubmit]);

  const title = isEditMode ? t('form.title_edit') : t('form.title_create');
  const buttonLabel = isEditMode ? t('actions.update') : t('actions.save');
  const readOnlyGoogle = isEditMode && isGoogleImportedEvent(editingEvent ?? {});

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={title}
      density="compact"
      footer={
        readOnlyGoogle ? null : (
          <EventDrawerSubmitButton
            label={buttonLabel}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />
        )
      }
    >
      {readOnlyGoogle ? (
        <Text
          className="mb-(--space-3) rounded-(--radius-md) bg-[var(--surface-secondary)] px-(--space-3) py-(--space-2) text-(--text-xs) text-[var(--text-secondary)]"
          data-testid="event-drawer-google-readonly-notice"
        >
          {t('drawer.google_readonly_notice')}
        </Text>
      ) : null}
      <EventDrawerFields
        form={form}
        calendarData={calendarData}
        recurrenceData={recurrenceData}
        allDay={allDay}
        calendarsCount={calendars.length}
        hasValidationErrors={hasValidationErrors}
        readOnly={readOnlyGoogle}
      />
    </AdaptiveFormShell>
  );
}
